import {
  useUpdateAlertsStatus,
} from '@gripp/shared-logic';
import { AlertsManagementTableData } from '@gripp/shared-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { AlertsStatusButton } from './alertsStatusButton';
import { EditAlertMenu } from './editAlertMenu';

type Props = {
  alerts: AlertsManagementTableData[];
  onStatusChanged: () => void;
};

/**
 * Side-panel bulk actions for alerts management:
 *   - Disable/Enable is shown when ≥1 alert is selected.
 *   - Edit Alert is shown only when exactly one alert is selected.
 */
export const AlertsSelectionPanelMain = ({
  alerts,
  onStatusChanged,
}: Props) => {
  const { t } = useTranslation();
  const { mutateAsync: updateAlertsStatus, isPending: isUpdating } =
    useUpdateAlertsStatus();

  // If every selected alert is already disabled, the button enables; otherwise
  // it disables.
  const isEnableAction = useMemo(
    () => alerts.length > 0 && alerts.every((a) => a.disabled),
    [alerts]
  );

  const alertIds = useMemo(() => alerts.map((a) => a.id), [alerts]);
  const singleAlertId = alerts.length === 1 ? alerts[0].id : undefined;

  const handleStatusChange = async () => {
    try {
      await updateAlertsStatus({
        input: {
          alertIds,
          disabled: !isEnableAction,
        },
      });
      onStatusChanged();
    } catch (error) {
      console.error('Failed to update alert status:', error);
    }
  };

  return (
    <View style={styles.container}>
      {alerts.length > 0 && (
        <AlertsStatusButton
          isEnableAction={isEnableAction}
          onPress={handleStatusChange}
          disabled={isUpdating}
        />
      )}
      {singleAlertId && (
        <EditAlertMenu
          alertId={singleAlertId}
          label={t('alertsManagement.menu.editAlert')}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 12,
  },
});
